import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';

function AdminTariffLevelsPage() {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const { token } = useAuth();
  
  // Состояние для создания нового уровня
  const [newLevelCode, setNewLevelCode] = useState('');
  const [newLevelName, setNewLevelName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Состояние для редактирования
  const [editingLevel, setEditingLevel] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDisplayOrder, setEditDisplayOrder] = useState(0);

  const fetchLevels = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/tariff-levels', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Не удалось загрузить уровни');
      const data = await response.json();
      setLevels(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchLevels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleCreateLevel = async (e) => {
    e.preventDefault();
    if (!newLevelCode.trim() || !newLevelName.trim()) {
      setError('Заполните все поля');
      return;
    }
    
    setSaving(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/tariff-levels', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'create',
          code: newLevelCode.trim().toLowerCase(),
          name: newLevelName.trim()
        })
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Ошибка создания');
      
      await fetchLevels();
      
      setNewLevelCode('');
      setNewLevelName('');
      setShowCreateForm(false);
      setSaveStatus('Уровень успешно создан');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditLevel = (level) => {
    setEditingLevel(level);
    setEditName(level.name);
    setEditDisplayOrder(level.display_order);
  };

  const handleUpdateLevel = async () => {
    if (!editingLevel) return;
    
    setSaving(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/tariff-levels', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'update',
          id: editingLevel.id,
          name: editName.trim(),
          display_order: editDisplayOrder
        })
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Ошибка обновления');
      
      await fetchLevels();
      
      setEditingLevel(null);
      setSaveStatus('Уровень успешно обновлен');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLevel = async (level) => {
    if (!window.confirm(`Удалить уровень "${level.name}"? Это действие нельзя отменить.`)) {
      return;
    }
    
    setSaving(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/tariff-levels', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'delete',
          id: level.id
        })
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Ошибка удаления');
      
      await fetchLevels();
      
      setSaveStatus('Уровень успешно удален');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main className="admin-page-content">
        <section>
          <div className="loading-full">Загрузка...</div>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-page-content">
      <section>
        <h2 className="admin-page-title">Управление уровнями тарифов</h2>
        <p className="admin-description-text">
          Здесь вы можете создавать и управлять уровнями тарифов (названиями). 
          Уровни используются при создании тарифов и отображаются на сайте и в мини-аппе.
        </p>
        
        {error && <div className="message-error">{error}</div>}
        {saveStatus && <div className="message-success">{saveStatus}</div>}
        
        <div style={{ marginBottom: '2rem' }}>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowCreateForm(!showCreateForm)}
            style={{ marginBottom: '1rem' }}
          >
            {showCreateForm ? '✖️ Отмена' : '➕ Создать новый уровень'}
          </button>
          
          {showCreateForm && (
            <form onSubmit={handleCreateLevel} style={{ 
              padding: '1.5rem', 
              border: '1px solid var(--border-color)', 
              borderRadius: '8px',
              marginBottom: '1.5rem',
              background: 'var(--bg-secondary)'
            }}>
              <h3 style={{ marginBottom: '1rem' }}>Создать новый уровень</h3>
              
              <div className="form-group">
                <label className="form-label">Код уровня (уникальный идентификатор)</label>
                <input
                  type="text"
                  value={newLevelCode}
                  onChange={(e) => setNewLevelCode(e.target.value)}
                  className="form-input"
                  placeholder="например: premium, vip, starter"
                  required
                  pattern="[a-z0-9_]+"
                  title="Только строчные буквы, цифры и подчеркивание"
                />
                <p className="form-hint">
                  Код должен быть уникальным и содержать только строчные буквы, цифры и подчеркивание.
                  Используется в системе для идентификации уровня.
                </p>
              </div>
              
              <div className="form-group">
                <label className="form-label">Название уровня</label>
                <input
                  type="text"
                  value={newLevelName}
                  onChange={(e) => setNewLevelName(e.target.value)}
                  className="form-input"
                  placeholder="например: Премиум, VIP, Стартовый"
                  required
                  maxLength={100}
                />
                <p className="form-hint">
                  Отображаемое название уровня. Будет показано пользователям на сайте и в мини-аппе.
                </p>
              </div>
              
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Создание...' : 'Создать уровень'}
              </button>
            </form>
          )}
        </div>
        
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Список уровней</h3>
          
          {levels.length === 0 ? (
            <div className="empty-state">
              <p>Нет созданных уровней тарифов.</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Код</th>
                    <th>Название</th>
                    <th>Порядок</th>
                    <th>По умолчанию</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {levels.map(level => (
                    <tr key={level.id}>
                      <td><code>{level.code}</code></td>
                      <td>{level.name}</td>
                      <td>{level.display_order}</td>
                      <td>{level.is_default ? '✅' : '—'}</td>
                      <td style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button 
                          className="btn-table-edit"
                          onClick={() => handleEditLevel(level)}
                          disabled={saving}
                        >
                          Редактировать
                        </button>
                        {!level.is_default && (
                          <button 
                            className="btn-table-delete"
                            onClick={() => handleDeleteLevel(level)}
                            disabled={saving}
                          >
                            Удалить
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {editingLevel && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              background: 'var(--bg-primary)',
              padding: '2rem',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '100%'
            }}>
              <h3 style={{ marginBottom: '1rem' }}>Редактировать уровень: {editingLevel.code}</h3>
              
              <div className="form-group">
                <label className="form-label">Название уровня</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="form-input"
                  maxLength={100}
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Порядок отображения</label>
                <input
                  type="number"
                  value={editDisplayOrder}
                  onChange={(e) => setEditDisplayOrder(parseInt(e.target.value) || 0)}
                  className="form-input"
                  min={0}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '10px', marginTop: '1.5rem' }}>
                <button 
                  className="btn btn-primary" 
                  onClick={handleUpdateLevel}
                  disabled={saving}
                >
                  {saving ? 'Сохранение...' : 'Сохранить'}
                </button>
                <button 
                  className="btn" 
                  onClick={() => setEditingLevel(null)}
                  disabled={saving}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}

export default AdminTariffLevelsPage;

